import { MouseEvent, MouseEventHandler, useContext, useEffect, useState } from 'react';

import { Box, Button, Center, Checkbox, Flex, Spinner, Text } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { cloneDeep } from 'lodash';
import { toast } from 'react-toastify';

import { Car, KbbSelectedOption, KbbSelectedOptionObject } from '../../gql/carGql';
import { Deal, DealStateEnum } from '../../gql/dealGql';
import {
  CbbValuation,
  Deal as GeneratedDeal,
  JdpVehicleAccessoriesObject,
  Maybe,
  useCalculateOptionsLazyQuery,
  useCbbValuesLazyQuery,
  useCbbVinLazyQuery,
  useJdpValuesLazyQuery,
  useKbbValuesLazyQuery,
  useKbbVinLazyQuery,
  useVehicleInfoUpsertMutation,
} from '../../gql/generated/graphql';

import Modal from '../shared/Modal';
import Tooltip from '../shared/Tooltip';

import { dealCountryFor, isCanada } from '../../constants/countries';
import { DealActionsEnum, DealContext } from '../../libs/DealContext';
import { logger } from '../../libs/Logger';
import {
  CbbVehicle,
  appliedUocCodes,
  cbbVehicleName,
  initialUocCodes,
  toCbbProvince,
  toggleUocCode,
  withTrimOptions,
} from '../../utils/cbb';
import { cleanDealForUpdate } from '../../utils/deals';

type Option = {
  categoryGroup: string;
  hasRelationships: boolean;
  isConfigurable: boolean;
  isConsumer: boolean;
  isTypical: boolean;
  isVinDecoded: boolean;
  optionName: string;
  optionType: string;
  sortOrder: number;
  vehicleId: number;
  vehicleOptionId: number;
};

type Vehicle = {
  makeName: string;
  modelName: string;
  trimName: string;
  vehicleId: number;
  vehicleName: string;
  vehicleOptions: Option[];
  yearId: number;
};

interface VehicleValuesProps {
  kbbSelectedOptions?: KbbSelectedOption[];
  shouldRefetchKbb: boolean;
  setShouldRefetchKbb: (value: boolean) => void;
  upsertOptions?: boolean;
}

const VehicleValuesCalculator = ({
  kbbSelectedOptions,
  shouldRefetchKbb,
  setShouldRefetchKbb,
  upsertOptions,
}: VehicleValuesProps) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [vehicle, setVehicle] = useState<Maybe<Vehicle>>();
  const [vehicleOptionIds, setVehicleOptionIds] = useState<number[]>([]);
  // Canadian Black Book: the trims for the VIN, the picked one, and its applied option codes.
  const [cbbVehicles, setCbbVehicles] = useState<CbbVehicle[]>([]);
  const [cbbVehicle, setCbbVehicle] = useState<CbbVehicle | null>(null);
  const [uocCodes, setUocCodes] = useState<string[]>([]);
  const { values, setFieldValue, setFieldTouched, errors, validateForm } = useFormikContext<Deal>();
  const { deal, dispatch } = useContext(DealContext);
  const [upsertVehicleInfo] = useVehicleInfoUpsertMutation();
  // The deal's country picks the valuation provider: KBB and JD Power in the US, CBB in Canada.
  const usesCbb = isCanada(dealCountryFor(values));
  const updateField = (
    name: string,
    value:
      | string
      | number
      | KbbSelectedOptionObject
      | JdpVehicleAccessoriesObject
      | CbbValuation
      | null
      | undefined,
  ) => {
    setFieldValue(name, value);
    setTimeout(() => setFieldTouched(name, true));
  };
  const [calculateOptions] = useCalculateOptionsLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.calculateOptions) {
        const selectedOptionIds =
          data?.calculateOptions?.map((opt) => Number(opt?.vehicleOptionId)) ?? [];

        setVehicleOptionIds(selectedOptionIds);

        setLoading(false);
      }
    },
  });

  // The following is used to disable the close button when options have been changed
  const [closeDisabled, setCloseDisabled] = useState(false);

  useEffect(() => {
    if (!showModal) {
      setCloseDisabled(false);
    }
  }, [showModal]);

  interface GetKbbValuesParams {
    vehicleId: number;
    vehicleOptions?: Option[];
    configToModify?: { optionId: number; action: string }[];
    startingVehicleOptions?: number[];
  }

  const getConfigurationOptions = async ({
    vehicleId,
    vehicleOptions,
    configToModify = [],
    startingVehicleOptions = [],
  }: GetKbbValuesParams) => {
    setLoading(true);

    await calculateOptions({
      variables: {
        vehicleId,
        allKbbVehicleOptions: vehicleOptions || vehicle?.vehicleOptions,
        configToModify,
        startingVehicleOptions,
        vin: values.car?.vin,
        color: values.car?.color,
      },
    });
  };

  const [kbbGetByVin, { loading: vinLoading }] = useKbbVinLazyQuery({
    fetchPolicy: 'cache-and-network',
    onCompleted: async (vinData) => {
      const vinResultsLength = vinData?.kbbVin?.vinResults?.length;

      if (!vinResultsLength) {
        toast.error('KBB vehicle not found');
        logger.error(
          'VehicleValuesCalculator.tsx',
          'kbbGetByVin',
          values.car.vin,
          'KBB vehicle not found',
        );
        setShowModal(false);
        return;
      }

      setVehicles(vinData.kbbVin?.vinResults as unknown as Vehicle[]);

      const vehicleData = vinData.kbbVin?.vinResults?.find(
        (veh) => veh?.trimName === values.car?.kbb_trim_name,
      ) as Maybe<Vehicle>;

      setVehicle(vehicleData);
      if (!vehicleData) {
        return;
      }

      const startingVehicleOptions =
        kbbSelectedOptions?.filter((opt) => !opt.removed)?.map((opt) => Number(opt.id)) ?? [];

      if (shouldRefetchKbb) {
        await getConfigurationOptions({
          vehicleOptions: vehicleData.vehicleOptions,
          vehicleId: vehicleData.vehicleId,
          startingVehicleOptions,
        });
      } else {
        setVehicleOptionIds(startingVehicleOptions);
      }

      // show warning
      if (vinData?.kbbVin?.warning) {
        toast('Warning: Double check VIN', {
          position: 'top-right',
          autoClose: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          type: 'warning',
        });
      }
    },
    onError: (e) => {
      toast.error('KBB unable to decode vin');
      logger.error('VehicleValuesCalculator.tsx', 'kbbGetByVin', values.car.vin, e);
      setShowModal(false);
    },
  });

  // Saves the form's deal with the calculated car values and folds the saved car back into
  // the deal context. Resolves to whether the save succeeded; the caller toasts.
  const upsertCarValues = async (carValues: Partial<GeneratedDeal['car']>): Promise<boolean> => {
    try {
      const newDeal = cleanDealForUpdate(values);
      const generatedNewDeal = newDeal as unknown as GeneratedDeal;
      const {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        deal_products: _dp,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        notifications: _n,
        ...dealWithoutProducts
      } = generatedNewDeal;

      const { data } = await upsertVehicleInfo({
        variables: {
          deal: {
            ...dealWithoutProducts,
            car: {
              ...generatedNewDeal.car,
              ...carValues,
            },
          },
        },
      });

      // Update DealContext with the saved deal data
      const updatedDeal = (data?.vehicleInfoUpsert ?? {}) as Deal;
      if (updatedDeal.car) {
        dispatch({
          type: DealActionsEnum.UpdateDeal,
          payload: {
            ...deal,
            car: {
              ...deal.car,
              ...updatedDeal.car,
            } as Car,
            financial_info_acquisition:
              deal.financial_info_acquisition || updatedDeal.financial_info_acquisition
                ? {
                    ...deal.financial_info_acquisition,
                    ...updatedDeal.financial_info_acquisition,
                  }
                : undefined,
          },
        });
      }
      return true;
    } catch (error) {
      logger.error('VehicleValuesCalculator.tsx', 'upsertVehicleInfo', null, error);
      return false;
    }
  };

  const [kbbGetValues, { loading: kbbValuesLoading }] = useKbbValuesLazyQuery({
    fetchPolicy: 'cache-and-network',
    onCompleted: async (valuesData) => {
      if (valuesData?.kbbValues?.lending && valuesData?.kbbValues?.retail) {
        updateField('car.retail_book_value', valuesData.kbbValues.retail);
        updateField(
          'car.kbb_retail_mileage_adjustment',
          valuesData.kbbValues.retailMileageAdjustment,
        );
        updateField(
          'car.kbb_retail_option_adjustment',
          valuesData.kbbValues.retailOptionAdjustment,
        );
        updateField('car.book_value', valuesData.kbbValues.lending);
        updateField(
          'car.kbb_lending_mileage_adjustment',
          valuesData.kbbValues.lendingMileageAdjustment,
        );
        updateField(
          'car.kbb_lending_option_adjustment',
          valuesData.kbbValues.lendingOptionAdjustment,
        );
        updateField('car.kbb_valuation_date', valuesData.kbbValues.valuationDate);
        updateField('car.kbb_vehicle_name', vehicle?.vehicleName);

        updateField('car.kbb_trim_name', vehicle?.trimName);

        updateField('car.kbb_selected_options_object', {
          selected_options: valuesData.kbbValues.kbbSelectedOptions as KbbSelectedOption[],
        });

        if (upsertOptions) {
          // Save vehicle info with KBB values to the database
          const saved = await upsertCarValues({
            book_value: valuesData.kbbValues.lending,
            retail_book_value: valuesData.kbbValues.retail,
            kbb_lending_mileage_adjustment: valuesData.kbbValues.lendingMileageAdjustment,
            kbb_retail_mileage_adjustment: valuesData.kbbValues.retailMileageAdjustment,
            kbb_lending_option_adjustment: valuesData.kbbValues.lendingOptionAdjustment,
            kbb_retail_option_adjustment: valuesData.kbbValues.retailOptionAdjustment,
            kbb_valuation_date: valuesData.kbbValues.valuationDate,
            kbb_vehicle_name: vehicle?.vehicleName,
            kbb_trim_name: vehicle?.trimName,
            kbb_selected_options_object: {
              selected_options: valuesData.kbbValues.kbbSelectedOptions as KbbSelectedOption[],
            },
          });
          if (!saved) {
            toast.error('Failed to save KBB values');
            setCloseDisabled(false);
            return;
          }
        }

        setShowModal(false);

        toast.success('KBB values updated');
      } else {
        toast.error('Unable to get KBB values');
        setCloseDisabled(false);
      }
    },
    onError: (e) => {
      toast.error('Unable to get KBB values');
      logger.error('VehicleValuesCalculator.tsx', 'kbbGetValues', null, e);
      setCloseDisabled(false);
    },
  });

  const [cbbGetByVin, { loading: cbbVinLoading }] = useCbbVinLazyQuery({
    fetchPolicy: 'cache-and-network',
    onCompleted: (vinData) => {
      const trims = vinData?.cbbVin?.vehicles ?? [];

      if (!trims.length) {
        toast.error('CBB vehicle not found');
        logger.error(
          'VehicleValuesCalculator.tsx',
          'cbbGetByVin',
          values.car.vin,
          'CBB vehicle not found',
        );
        setShowModal(false);
        return;
      }

      setCbbVehicles(trims);

      // Reopen on the saved trim with its saved options; otherwise the rep picks a trim.
      const saved = values.car?.cbb_valuation;
      const savedVehicle = trims.find((trim) => trim.uvc === saved?.uvc) ?? null;
      setCbbVehicle(savedVehicle);
      setUocCodes(initialUocCodes(savedVehicle, saved));

      if (vinData?.cbbVin?.warning) {
        toast('Warning: Double check VIN', {
          position: 'top-right',
          autoClose: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          type: 'warning',
        });
      }
    },
    onError: (e) => {
      toast.error('CBB unable to decode vin');
      logger.error('VehicleValuesCalculator.tsx', 'cbbGetByVin', values.car.vin, e);
      setShowModal(false);
    },
  });

  const [cbbGetValues, { loading: cbbValuesLoading }] = useCbbValuesLazyQuery({
    // CBB bills this call, so it is never answered from the cache.
    fetchPolicy: 'network-only',
    onCompleted: async (valuesData) => {
      const priced = valuesData?.cbbValues;
      // Null is CBB's answer for a trim it cannot value; nothing is saved, as with KBB.
      if (!priced?.lending || !priced.retail) {
        toast.error('Unable to get CBB values');
        setCloseDisabled(false);
        return;
      }

      // Trade-in clean is the lending value and retail clean the retail value, as the API
      // maps them at deal creation. The whole record is stored beside them.
      const valuation = withTrimOptions(priced, cbbVehicle?.add_deduct_list ?? [], uocCodes);
      const carValues: Partial<Car> = {
        book_value: priced.lending.adjusted,
        retail_book_value: priced.retail.adjusted,
        cbb_valuation: valuation,
      };
      updateField('car.book_value', carValues.book_value);
      updateField('car.retail_book_value', carValues.retail_book_value);
      updateField('car.cbb_valuation', valuation);

      if (upsertOptions) {
        const saved = await upsertCarValues(carValues);
        if (!saved) {
          toast.error('Failed to save CBB values');
          setCloseDisabled(false);
          return;
        }
      }

      setShowModal(false);
      toast.success('CBB values updated');
    },
    onError: (e) => {
      toast.error('Unable to get CBB values');
      logger.error('VehicleValuesCalculator.tsx', 'cbbGetValues', null, e);
      setCloseDisabled(false);
    },
  });

  const [jdpGetValues, { loading: jdpValuesLoading }] = useJdpValuesLazyQuery({
    fetchPolicy: 'cache-and-network',
    onCompleted: (jdpData) => {
      if (jdpData?.jdpValues) {
        updateField('car.jdp_mileage_adjustment', jdpData.jdpValues.jdp_mileage_adjustment);
        updateField('car.jdp_adjusted_clean_retail', jdpData.jdpValues.jdp_adjusted_clean_retail);
        updateField('car.jdp_adjusted_clean_trade', jdpData.jdpValues.jdp_adjusted_clean_trade);
        updateField('car.jdp_trim_body', jdpData.jdpValues.jdp_trim_body);
        updateField('car.jdp_valuation_date', jdpData.jdpValues.jdp_valuation_date);
        updateField(
          'car.jdp_vehicle_accessories_object',
          jdpData.jdpValues.jdp_vehicle_accessories_object,
        );

        toast.success('JDP values updated');
      }
    },
    onError: (e) => {
      toast.error('Unable to get JDP values');
      logger.error('VehicleValuesCalculator.tsx', 'jdpGetValues', null, e);
    },
  });

  const openModal = async () => {
    if (usesCbb) {
      // The last pick belongs to the VIN it was looked up for; the new lookup restores it
      // only when that trim is still listed, so nothing stale can be priced meanwhile.
      setCbbVehicle(null);
      setUocCodes([]);
      // The deal id tags the lookup in CBB's activity report; a new estimate has none yet.
      cbbGetByVin({ variables: { vin: values.car.vin, dealId: values.id } });
    } else {
      kbbGetByVin({
        variables: {
          vin: values.car.vin,
        },
      });
    }
    setShowModal(true);
  };

  const changeConfiguration = (id: number, action: string) => {
    setCloseDisabled(true);
    getConfigurationOptions({
      vehicleId: vehicle?.vehicleId as number,
      configToModify: [{ optionId: id, action }],
      startingVehicleOptions: vehicleOptionIds,
    });
  };

  const calculateCbbValues = () => {
    const province = toCbbProvince(values.customer?.address?.state);
    if (!cbbVehicle) {
      toast.error('Choose a trim before calculating values');
      return;
    }
    if (!province) {
      toast.error('Set the customer province before calculating values');
      return;
    }
    // Kilometres go to CBB as entered; a Canadian deal only has a kilometres field. CBB bills
    // the call, so a blank field never becomes a 0 km valuation.
    const kilometres = values.car.mileage;
    if (!kilometres) {
      toast.error('Enter the kilometres before calculating values');
      return;
    }
    setCloseDisabled(true);
    cbbGetValues({
      variables: {
        data: {
          vin: values.car.vin,
          uvc: cbbVehicle.uvc,
          kilometres,
          province,
          uocCodes,
          dealId: values.id,
        },
      },
    });
  };

  const calculateValues = () => {
    if (usesCbb) {
      calculateCbbValues();
      return;
    }
    setShouldRefetchKbb(false);
    kbbGetValues({
      variables: {
        data: {
          allKbbVehicleOptions: vehicle?.vehicleOptions,
          mileage: values.car.mileage,
          vehicleOptionIds,
          vehicleId: vehicle?.vehicleId,
        },
      },
    });
    jdpGetValues({
      variables: {
        vin: values.car.vin,
        mileage: values.car.mileage ?? 0,
        stateCode: values.customer.address.state,
      },
    });
  };

  const handleVehicleClick = async (e: MouseEvent<HTMLDivElement>, selectedVehicle: Vehicle) => {
    e.stopPropagation();
    setVehicle(selectedVehicle);

    await getConfigurationOptions({
      vehicleOptions: selectedVehicle.vehicleOptions,
      vehicleId: selectedVehicle.vehicleId,
      startingVehicleOptions: [],
    });
  };

  const handleCbbVehicleClick = (e: MouseEvent<HTMLDivElement>, selectedVehicle: CbbVehicle) => {
    e.stopPropagation();
    setCbbVehicle(selectedVehicle);
    setUocCodes(appliedUocCodes(selectedVehicle.add_deduct_list));
  };

  // Back to the trim list for either provider.
  const resetTrimSelection = () => {
    setVehicle(null);
    setVehicleOptionIds([]);
    setCbbVehicle(null);
    setUocCodes([]);
  };

  const handleDifferentVehicleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    resetTrimSelection();
  };

  // This useEffect is here because the book_value/KBB Lending field was showing an error even after updating the field in the useEffect above
  useEffect(() => {
    if (showModal === false) {
      validateForm();
    }
  }, [showModal, validateForm]);

  const renderOptions = () => {
    const sortedByGroup =
      vehicle?.vehicleOptions?.reduce((prev, curr) => {
        const previous = cloneDeep(prev);
        if (!prev[curr.categoryGroup]) {
          previous[curr.categoryGroup] = [];
        }
        previous[curr.categoryGroup].push(curr);

        return previous;
      }, {} as { [x: string]: Option[] }) ?? {};

    return Object.keys(sortedByGroup).map((key) => (
      <Box mb="25px" key={key}>
        <Text fontWeight="bold" mb={2} fontSize="lg">
          {key}
        </Text>
        {sortedByGroup[key].map((o) => {
          const selected = vehicleOptionIds.includes(o.vehicleOptionId);

          return (
            <Flex
              justifyContent="space-between"
              alignItems="center"
              mb="5px"
              pb="5px"
              borderBottom="1px solid rgba(0,0,0,0.1)"
              key={o.vehicleOptionId}
            >
              <Text>{o.optionName}</Text>
              <Checkbox
                m={0}
                size="lg"
                type="checkbox"
                id="optionSelected"
                isDisabled={loading}
                isChecked={selected}
                onChange={() => {
                  changeConfiguration(o.vehicleOptionId, !selected ? 'selected' : 'deselected');
                }}
              />
            </Flex>
          );
        })}
      </Box>
    ));
  };

  const renderVehicle = () => {
    return (
      <Flex flexWrap="wrap">
        {vehicles.map((v) => (
          <Box
            p={4}
            bg="white"
            shadow="md"
            borderRadius="md"
            flex="0 0 250px"
            _hover={{ shadow: 'lg', bg: 'gray.50' }}
            cursor="pointer"
            key={v.vehicleId}
            onClick={(e: MouseEvent<HTMLDivElement>) => handleVehicleClick(e, v)}
          >
            <Text fontWeight="bold">{v.vehicleName}</Text>
            <p>{v.trimName}</p>
            <p>{v.modelName}</p>
            <p>{v.yearId}</p>
          </Box>
        ))}
      </Flex>
    );
  };

  // CBB options have no categories and no conflicts: one flat list, toggled locally and
  // sent with the values call.
  const renderCbbOptions = () => {
    const options = cbbVehicle?.add_deduct_list ?? [];
    if (!options.length) {
      return <Text>Canadian Black Book lists no options for this trim.</Text>;
    }
    return (
      <Box mb="25px">
        {options.map((option) => {
          const selected = uocCodes.includes(option.uoc);
          return (
            <Flex
              justifyContent="space-between"
              alignItems="center"
              mb="5px"
              pb="5px"
              borderBottom="1px solid rgba(0,0,0,0.1)"
              key={option.uoc}
            >
              <Text>{option.name}</Text>
              <Checkbox
                m={0}
                size="lg"
                type="checkbox"
                aria-label={option.name}
                isDisabled={cbbValuesLoading}
                isChecked={selected}
                onChange={() => setUocCodes(toggleUocCode(options, uocCodes, option.uoc))}
              />
            </Flex>
          );
        })}
      </Box>
    );
  };

  const renderCbbVehicles = () => {
    return (
      <Flex flexWrap="wrap">
        {cbbVehicles.map((trim) => (
          <Box
            p={4}
            bg="white"
            shadow="md"
            borderRadius="md"
            flex="0 0 250px"
            _hover={{ shadow: 'lg', bg: 'gray.50' }}
            cursor="pointer"
            key={trim.uvc}
            onClick={(e: MouseEvent<HTMLDivElement>) => handleCbbVehicleClick(e, trim)}
          >
            <Text fontWeight="bold">{cbbVehicleName(trim)}</Text>
            <p>{trim.trim_name}</p>
          </Box>
        ))}
      </Flex>
    );
  };

  const showOptions = usesCbb ? !!cbbVehicle : !!vehicle;
  const isTrimLookupLoading = usesCbb ? cbbVinLoading : vinLoading;
  const isValuesLoading = usesCbb ? cbbValuesLoading : jdpValuesLoading || kbbValuesLoading;

  // CBB prices a trim by kilometres and province; only KBB needs the colour.
  const calcKbbButtonErrors = [
    errors.customer?.address?.state,
    errors.customer?.address?.zip,
    errors.car?.vin,
    errors.car?.mileage,
    ...(usesCbb ? [] : [errors.car?.color]),
  ];
  const disabled = calcKbbButtonErrors.some(Boolean);

  return (
    <>
      <Box>
        <Tooltip errors={calcKbbButtonErrors}>
          <Button
            size="sm"
            isLoading={isTrimLookupLoading}
            loadingText="CALCULATE VALUES"
            isDisabled={disabled}
            onClick={openModal}
          >
            CALCULATE VALUES
          </Button>
        </Tooltip>
      </Box>
      <Modal
        closeOnOverlayClick={false}
        closeOnEsc={false}
        canDismiss={false}
        title={showOptions ? 'Vehicle Options' : 'Choose a Vehicle'}
        isOpen={showModal}
        size="2xl"
        variant="noMargin"
        onClose={() => {
          resetTrimSelection();
          setShowModal(false);
        }}
        rightButtons={
          <>
            <Button
              variant="secondary"
              disabled={closeDisabled}
              onClick={() => setShowModal(false)}
            >
              CLOSE
            </Button>
            {showOptions ? (
              <Button
                variant="secondary"
                onClick={handleDifferentVehicleClick as MouseEventHandler}
                disabled={values.state === DealStateEnum.Closed}
              >
                CHANGE TRIM
              </Button>
            ) : null}

            <Button
              isLoading={isValuesLoading}
              loadingText="CALCULATE VALUES"
              // A CBB request is billed, so none goes out while the trims for the current
              // VIN are still loading or before one of them is picked.
              isDisabled={isTrimLookupLoading || (usesCbb && !cbbVehicle)}
              onClick={calculateValues}
            >
              CALCULATE VALUES
            </Button>
          </>
        }
      >
        {isTrimLookupLoading ? (
          <Center h="300px">
            <Spinner boxSize="100px" />
          </Center>
        ) : showOptions ? (
          usesCbb ? (
            renderCbbOptions()
          ) : (
            renderOptions()
          )
        ) : usesCbb ? (
          renderCbbVehicles()
        ) : (
          renderVehicle()
        )}
      </Modal>
    </>
  );
};

export default VehicleValuesCalculator;
